import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import ProductForm from "./ProductForm";

import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import {
  useGetSingleProductQuery,
  useMutateProductsMutation,
} from "@/redux/api/toolsQuery";
import {
  TUpdateProduct,
  defaultProduct,
  updateProductSchema,
} from "@/schemas/tools.schema";
import {
  filterEmptyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface UpdateProductModalProps {
  isOpen: boolean;
  toggleUpdateProductModal: () => void;
}

const UpdateProductModal = ({
  isOpen,
  toggleUpdateProductModal,
}: UpdateProductModalProps): JSX.Element => {
  const { id, reset } = useQueryID(ToolsKeys.PRODUCT_ASSIGNMENT_ID);

  const { data, isLoading } = useGetSingleProductQuery({
    id,
  });
  const singleProduct = data?.data;
  const {
    FORM: { PRODUCT },
  } = ToolsConstants;

  const [updateProduct, { isLoading: isProductUpdating }] =
    useMutateProductsMutation();

  const form = useForm<TUpdateProduct>({
    resolver: zodResolver(updateProductSchema),
    ...(singleProduct && {
      values: {
        id: singleProduct?.id,
        name: singleProduct?.name || undefined,
        files: singleProduct?.files || [],
        images: singleProduct?.images || [],
        quantity: singleProduct?.quantity || 0,
        type: singleProduct?.type,
      },
    }),
    defaultValues: defaultProduct,
  });

  const handleExit = (): void => {
    reset();
    form.reset();
    toggleUpdateProductModal();
  };

  const onSubmit = async (data: TUpdateProduct): Promise<void> => {
    const existingImages =
      Array.isArray(data?.images) &&
      data?.images?.filter(
        (image) => typeof image === "string" && image.startsWith("http"),
      );

    const existingFiles =
      Array.isArray(data?.files) &&
      data?.files?.filter(
        (file) => typeof file === "string" && file.startsWith("http"),
      );

    const images = data.images?.filter((image) => typeof image !== "string");
    if (!images || (Array.isArray(images) && images.length === 0)) {
      delete data.images;
    }
    const files = data.files?.filter((file) => typeof file !== "string");
    if (!files || (Array.isArray(files) && files.length === 0)) {
      delete data.files;
    }

    const dataToSent = parseToFormData(
      filterEmptyValues(data),
      ["files", "images"],
      form.formState.dirtyFields,
    );

    if (Array.isArray(existingImages) && existingImages?.length > 0) {
      dataToSent.append("existingImages", JSON.stringify(existingImages));
    }
    if (Array.isArray(existingFiles) && existingFiles?.length > 0) {
      dataToSent.append("existingFiles", JSON.stringify(existingFiles));
    }

    const response = await updateProduct({
      method: "PUT",
      productId: id,
      data: dataToSent,
    });

    validateResponse({
      response,
      onSuccess: () => {
        handleExit();
        toast.success(PRODUCT.UPDATE_PRODUCT_SUCCESS);
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={PRODUCT.UPDATE_PRODUCT}
      toggleModal={handleExit}
      className='md:max-w-3xl '
      isLoading={isLoading}
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form className=' space-y-6' onSubmit={form.handleSubmit(onSubmit)}>
          <ProductForm disableField={true} />
          <div className=' flex justify-between'>
            <Button
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isProductUpdating}
            >
              {Button_Names.Discard_Change}
            </Button>
            <Button
              type='submit'
              className='w-[300px]'
              isLoading={isProductUpdating}
              disabled={!form.formState.isDirty}
            >
              {Button_Names.Save_Changes}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default UpdateProductModal;
